import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import ICImage from "../../core-component/ui/ICImage";
import ICOtpInput from "../../core-component/ui/ICOtpInput";
import ICTimer from "../../core-component/ui/ICTimer";
import {
  ILoginSessionLimit,
  initialValuesOtp,
  resendOtpApi,
  submitOtpApi,
} from "./verifyOtpController";
import LoginSessionSelectModal from "./LoginSessionSelectModal";
import { ICButton } from "../../core-component/ui";
import { editIcon, leftArrow } from "../../assets/icons/SvgIconList";
import { routePublic } from "../../routes/PublicRoutePath";
import { decryptData } from "../../utils/AppFunctions";
import { BIT_VALUE } from "../../utils/AppEnumerations";

const VerifyOTP = () => {
  const { t } = UseTranslationHook();
  const navigation = useNavigate();
  const location = useLocation();

  const [otpValue, setOtpValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionLimit, setSessionLimit] = useState<ILoginSessionLimit | null>(
    null
  );
  const searchParams = new URLSearchParams(location.search);
  const decryptParams = decryptData(searchParams.get("us") ?? "");

  const [userName, isForgot] = decryptParams.split("|");
  const handleOtpChange = (otp: string) => {
    setOtpValue(otp);
  };

  const formik = useFormik({
    initialValues: initialValuesOtp,
    onSubmit: async (values) => {
      submitOtpApi(
        { ...values, otp: otpValue, username: userName },
        navigation,
        setLoading,
        t,
        isForgot,
        setSessionLimit,
      );
      setOtpValue("");
    },
  });

  const resendOTP = () => {
    resendOtpApi({ username: userName }, t);
    setOtpValue("");
  };

  return (
    <div>
      <div className="grid grid-cols-12">
        <div className="col-span-12 hidden md:flex md:col-span-6 lg:col-span-7 xl:col-span-8 h-screen w-full items-center justify-center bg-base">
          <ICImage
            className="w-full "
            src={require("../../assets/image/app-logo.png")}
            alt={t("poster_image")}
          />
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-5 xl:col-span-4 h-screen flex items-center justify-center bg-subtle">
          <div className="mx-6 sm:mx-6 md:mx-14 lg:mx-20 xl:mx-24 xxl:mx-28">
            <div className="flex w-full justify-center">
              <ICImage
                src={require("../../assets/image/app-logo-server.jpg")}
                className="w-full"
              />
            </div>
            <div className="text-center mt-5">
              <p className="font-bold text-3xl">{t("otp_verification")}</p>
              <p>{t("otp_on_your_mobile_number")}</p>
            </div>
            <div className="flex mt-3 items-center justify-center text-center">
              <p className="mx-2">{userName}</p>
              <p
                className="cursor-pointer w-5 h-5"
                onClick={() => {
                  isForgot === BIT_VALUE.One
                    ? navigation(`/${routePublic.forgotPassword}`, {
                        state: { username: userName },
                      })
                    : navigation(`/${routePublic.login}`, {
                        state: { username: userName },
                      });
                }}
              >
                {editIcon}
              </p>
            </div>
            <form className="mt-5" onSubmit={formik.handleSubmit}>
              <div className="relative mb-4 flex items-center justify-center">
                <ICOtpInput
                  value={otpValue}
                  onChange={handleOtpChange}
                  numInputs={4}
                />
              </div>

              <div className="mb-2 flex justify-center">
                <ICTimer
                  initialTime="0:30"
                  maxResend={3}
                  onResend={resendOTP}
                />
              </div>
              <div className="text-center lg:text-left">
                <ICButton
                  type="submit"
                  loading={loading}
                  children={t("verify_and_continue")}
                  className={`!w-full  ${
                    otpValue.length !== 4
                      ? "cursor-not-allowed auth-padding text-white"
                      : "auth-btn"
                  }`}
                  data-te-ripple-init
                  data-te-ripple-color="light"
                  disabled={otpValue.length !== 4}
                />
              </div>
              <div
                className="text-muted flex items-center justify-center cursor-pointer mt-2"
                onClick={() => {
                  navigation("/" + routePublic.login);
                }}
              >
                <div className="size-4">{leftArrow}</div>
                <div className="ml-2 text-sm">{t("back_to_log_in")}</div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {sessionLimit && (
        <LoginSessionSelectModal
          sessionLimit={sessionLimit}
          navigation={navigation}
          onClose={() => {
            setSessionLimit(null);
            navigation("/" + routePublic.login, {
              state: { username: userName },
            });
          }}
        />
      )}
    </div>
  );
};

export default VerifyOTP;
